/**
 * Efectos de sonido PROCEDURALES (Web Audio API), generados en código.
 * Sin ficheros de audio: cada sonido es un oscilador con envolvente.
 * El navegador exige un gesto del usuario para arrancar el audio; se
 * desbloquea solo en el primer clic o tecla.
 */
class SfxEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const AC: typeof AudioContext | undefined =
        window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.34; // volumen general
      this.master.connect(this.ctx.destination);
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  /** Reanuda el contexto (tras gesto del usuario). */
  unlock(): void {
    const c = this.ensure();
    if (c && c.state === "suspended") void c.resume();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  /** Un tono con envolvente rápida (ataque-caída). */
  private tone(freq: number, dur: number, type: OscillatorType = "sine", vol = 0.5, slideTo?: number, delay = 0): void {
    if (this.muted) return;
    const c = this.ensure();
    if (!c || !this.master) return;
    if (c.state === "suspended") void c.resume(); // reintento de desbloqueo en cada sonido
    const t = c.currentTime + delay;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + dur);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.03);
  }

  // ---- paleta de sonidos del juego ----
  click(): void { this.tone(420, 0.05, "triangle", 0.4); }
  inspect(): void { this.tone(540, 0.08, "sine", 0.45); }
  pickup(): void { this.tone(660, 0.1, "triangle", 0.45, 880); }
  talk(): void { this.tone(360, 0.06, "sine", 0.35); }
  step(): void { this.tone(180, 0.04, "sine", 0.25, 120); }
  good(): void { this.tone(600, 0.1, "sine", 0.45); this.tone(880, 0.14, "sine", 0.4, undefined, 0.09); }
  warn(): void { this.tone(280, 0.16, "sawtooth", 0.35, 170); }
  /** Paso correcto de un minijuego: tic breve ascendente. */
  tick(): void { this.tone(760, 0.06, "triangle", 0.4, 1020); }
  /** Secuencia completada (consignación, PAS, aplicar medida...). */
  done(): void { [659, 880, 1175].forEach((f, i) => this.tone(f, 0.16, "sine", 0.4, undefined, i * 0.08)); }
  /** Paso de EPI al caminar (alterna dos tonos). */
  foot(hi = false): void { this.tone(hi ? 150 : 120, 0.035, "sine", 0.14, hi ? 105 : 85); }
  measure(): void { this.tone(500, 0.05, "square", 0.25); this.tone(500, 0.05, "square", 0.25, undefined, 0.12); }
  resolve(): void { [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.2, "sine", 0.4, undefined, i * 0.11)); }
  unlockChime(): void { this.tone(784, 0.12, "triangle", 0.4); this.tone(1175, 0.16, "triangle", 0.35, undefined, 0.1); }
}

export const Sfx = new SfxEngine();

// Desbloqueo automático en el primer gesto del usuario. Escucha en fase de
// CAPTURA (antes de que Phaser pueda frenar el evento del lienzo) y con varios
// tipos de gesto, para que el audio arranque sí o sí en el primer clic/tecla.
if (typeof window !== "undefined") {
  const unlock = (): void => Sfx.unlock();
  const evs = ["pointerdown", "mousedown", "touchstart", "keydown", "click"] as const;
  for (const ev of evs) window.addEventListener(ev, unlock, { capture: true, passive: true });
  document.addEventListener("pointerdown", unlock, { capture: true, passive: true });
  // reanuda si el navegador suspende el audio (cambio de pestaña, etc.)
  document.addEventListener("visibilitychange", () => { if (!document.hidden) Sfx.unlock(); });
}
